/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import { ChevronRightIcon } from '@/shared/ui/radix-icons';
import { blockCardStyle } from './settings-block-styles';
import type { SettingsItemKey } from '../constants/settings-items';

/** What a setting's row says about its current value. */
export type ItemSummary =
  | { state: 'ready'; text: string }
  | { state: 'loading' }
  | { state: 'error' };

export type ItemSummaries = Partial<Record<SettingsItemKey, ItemSummary>>;

/** What a tab with items receives: the open item, and how to open one. */
export interface ItemSectionProps {
  item?: SettingsItemKey | null;
  onOpenItem?: (item: SettingsItemKey) => void;
}

/**
 * A summary from a query's state: the text only once the data is there.
 *
 * @param query - The load state of the data the summary reads.
 * @param text - Builds the text; only called when the data is ready.
 * @returns The summary.
 */
export function summaryOf(
  query: { isLoading: boolean; isError: boolean },
  text: () => string,
): ItemSummary {
  if (query.isLoading) return { state: 'loading' };
  if (query.isError) return { state: 'error' };
  return { state: 'ready', text: text() };
}

interface SettingsItemListProps {
  items: SettingsItemKey[];
  summaries: ItemSummaries;
  onOpen: (item: SettingsItemKey) => void;
}

/**
 * The words a row shows on its right: the value, or why there is none yet.
 *
 * @param summary - The item's summary, if the tab computes one.
 * @param t - The settings translator.
 * @returns The text and whether it is a placeholder.
 */
function summaryText(
  summary: ItemSummary | undefined,
  t: (key: string) => string,
): { text: string; muted: boolean } {
  if (!summary || summary.state === 'loading') return { text: t('v2.items.loading'), muted: true };
  if (summary.state === 'error') return { text: t('v2.items.loadError'), muted: true };
  return { text: summary.text, muted: false };
}

/**
 * A tab's settings, one row each: what it is on the left, its current value
 * on the right. The values are there so the user can check a setting without
 * opening it, and a tab with fifteen settings still fits on one screen.
 *
 * @returns The list card.
 */
export function SettingsItemList({ items, summaries, onOpen }: SettingsItemListProps) {
  const { t } = useTranslation('features/agency-settings');

  return (
    <div style={blockCardStyle} data-testid="settings-item-list">
      {items.map((item, index) => {
        const { text, muted } = summaryText(summaries[item], t);
        return (
          <UnstyledButton
              key={item}
              type="button"
              onClick={() => onOpen(item)}
              data-testid={`settings-item-${item}`}
              className="flex w-full items-center gap-(--space-4) text-left transition-colors hover:bg-(--surface-hover)"
              style={{
                minHeight: 64,
                padding: 'var(--space-3) var(--space-5)',
                borderBottom: index === items.length - 1 ? 'none' : '1px solid var(--border-ultra-subtle)',
                fontFamily: 'var(--font-family)',
              }}
            >
              <Text component="span" style={{ flex: '1 1 auto', minWidth: 0 }}>
                <Text component="span" style={{ display: 'block', fontSize: 'var(--text-body-sm)', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {t(`v2.items.${item}.title`)}
                </Text>
                <Text component="span" style={{ display: 'block', marginTop: 2, fontSize: 'var(--text-meta)', lineHeight: 1.5, color: 'var(--text-tertiary-soft)' }}>
                  {t(`v2.items.${item}.description`)}
                </Text>
              </Text>
              <Text
                component="span"
                data-testid={`settings-item-${item}-summary`}
                style={{
                  flex: '0 1 auto', maxWidth: '45%', textAlign: 'right',
                  fontSize: 'var(--text-body-sm)',
                  color: muted ? 'var(--text-tertiary-soft)' : 'var(--text-primary)',
                }}
              >
                {text}
              </Text>
              <ChevronRightIcon width={18} height={18} aria-hidden="true" style={{ flex: 'none', color: 'var(--text-tertiary-soft)' }} />
            </UnstyledButton>
        );
      })}
    </div>
  );
}
